import { Link } from "react-router-dom";
import { ArrowRight, Phone, ArrowUpRight } from "lucide-react";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import LogoFull from "@/components/ui/LogoFull";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { TRACK_LIST, TRACKS, getPreferredTrack, setPreferredTrack } from "@/lib/tracks";
import { PAGE_META } from "@/lib/seo-constants";
import { localBusinessSchema, websiteSchema } from "@/lib/jsonld";
import { usePageContentOverrideMap } from "@/hooks/usePageContentOverrides";

const TrackCard = ({ track, override }) => (
    <Link
        to={track.home}
        onClick={() => setPreferredTrack(track.key)}
        className="group relative flex flex-col justify-end overflow-hidden border border-white/10 bg-brand-dark min-h-[320px] md:min-h-[420px] transition-all duration-200 hover:border-brand-cyan hover:-translate-y-1"
        data-testid={`splash-card-${track.key}`}
    >
        <img
            src={override?.hero_image || track.image}
            alt={
                override?.hero_image_alt ||
                `${track.label} contracting — ${track.cardTitle}`
            }
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-500 group-hover:scale-105"
        />
        <div
            className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/55 to-brand-dark/10"
            aria-hidden="true"
        />
        <div className="relative z-10 p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-2">
                {track.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white leading-none">
                {track.cardTitle}
            </h2>
            <p className="mt-3 text-sm text-white/75 leading-relaxed max-w-sm">
                {track.cardBlurb}
            </p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white group-hover:text-brand-cyan transition-colors">
                Enter {track.label}
                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </span>
        </div>
    </Link>
);

export default function Splash() {
    const preferred = getPreferredTrack();
    const preferredTrack = preferred ? TRACKS[preferred] : null;
    const overrideMap = usePageContentOverrideMap();

    return (
        <>
            <Seo
                {...PAGE_META.splash}
                path="/"
                jsonLd={[localBusinessSchema(), websiteSchema()]}
            />

            <section
                className="relative bg-brand-dark text-white"
                data-testid="splash"
            >
                <Container className="py-14 md:py-20">
                    {/* Brand + headline */}
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="flex justify-center mb-8">
                            <LogoFull theme="dark" className="h-14 md:h-16" />
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-white">
                            Built for every property.
                        </h1>
                        <p className="mt-5 text-base md:text-lg text-white/75 font-light italic">
                            Residential · Commercial · Industrial — exterior &amp; general
                            contracting across the GTA.
                        </p>

                        {/* Return-visit soft suggestion (never traps — all three still shown below) */}
                        {preferredTrack && (
                            <div className="mt-6">
                                <Link
                                    to={preferredTrack.home}
                                    onClick={() => setPreferredTrack(preferredTrack.key)}
                                    className="inline-flex items-center gap-2 border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-2 text-sm font-semibold text-brand-cyan hover:bg-brand-cyan hover:text-brand-dark transition-colors"
                                    data-testid="splash-continue"
                                >
                                    Continue to {preferredTrack.label}
                                    <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Three track cards */}
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                        {TRACK_LIST.map((t) => (
                            <TrackCard
                                key={t.key}
                                track={t}
                                override={overrideMap[`splash-card:${t.key}`]}
                            />
                        ))}
                    </div>

                    {/* Persistent CTAs */}
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <BrandButton asChild variant="primary" size="lg">
                            <Link to="/contact" data-testid="splash-cta-quote">
                                Get a Free Quote
                                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                            </Link>
                        </BrandButton>
                        <a
                            href={COMPANY.phoneHref}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-brand-cyan transition-colors"
                            data-testid="splash-phone"
                        >
                            <Phone className="h-4 w-4 text-brand-coral" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </div>

                    {/* Crawlable intro — link equity to all three tracks (not a JS-only dead-end) */}
                    <div className="mt-14 pt-10 border-t border-white/10 max-w-3xl mx-auto text-center">
                        <p className="text-sm text-white/60 leading-relaxed">
                            <strong className="text-white/80">Reformity Inc.</strong> is a
                            North York-based exterior and general contracting company serving
                            the Greater Toronto Area. We build and maintain fences, decks,
                            interlocking, concrete, asphalt, landscaping, masonry and
                            interior renovations for{" "}
                            <Link to="/residential" className="text-brand-cyan hover:underline">
                                residential homeowners
                            </Link>
                            ,{" "}
                            <Link to="/commercial" className="text-brand-cyan hover:underline">
                                commercial property owners and managers
                            </Link>
                            , and{" "}
                            <Link to="/industrial" className="text-brand-cyan hover:underline">
                                industrial facilities
                            </Link>{" "}
                            across the GTA. Licensed, insured, and WSIB-compliant.
                        </p>
                    </div>
                </Container>
            </section>
        </>
    );
}
